import React from "react";
import * as ReactDOM from "react-dom/client";
import "./landing-shared.entry.jsx";
const SiteNav = globalThis.SiteNav ?? globalThis.window?.SiteNav;
const SiteFooter = globalThis.SiteFooter ?? globalThis.window?.SiteFooter;
// Contact page
const { useState } = React;

const Icon = {
  arrow: (p) => <svg width="12" height="12" viewBox="0 0 12 12" fill="none" {...p}><path d="M3 6h6m0 0L6 3m3 3L6 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square"/></svg>,
};

function Hero() {
  return (
    <section className="section section-dark contact-hero">
      <div className="dark-grid"></div>
      <div className="dark-glow"></div>
      <div className="container">
        <span className="ch-eyebrow"><span className="eb-dot"></span>CONTACT · WE ANSWER · USUALLY THE SAME DAY</span>
        <h1>Get in touch.<br/>One inbox,<br/><em>clear replies.</em></h1>
        <p>Use the contact page when you need help, have a pricing question, or want to tell us something is broken.</p>
      </div>
    </section>
  );
}

function Channels() {
  const channels = [
    { tag: 'CONTACT', title: 'Use one address for everything.', body: 'Send product questions, support requests, pricing notes, and bug reports to one inbox. If the dashboard is available, including workspace details helps us reply faster.', link: 'mailto:contact@named.network', linkLabel: 'contact@named.network', meta: 'REPLY · USUALLY SAME DAY' },
  ];
  return (
    <section className="channels">
      <div className="dark-grid"></div>
      <div className="container">
        <div className="channels-grid">
          {channels.map((c, i) => (
            <div key={i} className="channel">
              <div className="c-tag">{c.tag}</div>
              <h3>{c.title}</h3>
              <p>{c.body}</p>
              <a className="c-link" href={c.link}>{c.linkLabel} <Icon.arrow /></a>
              <div className="c-meta">{c.meta}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactForm() {
  const topics = ['Product question', 'Support', 'Pricing', 'Bug report', 'Other'];
  const [topic, setTopic] = useState('Product question');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [teamSize, setTeamSize] = useState('Just me');
  const [message, setMessage] = useState('');
  const [draftReady, setDraftReady] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();
    const subject = `[${topic}] ${name || 'Contact form'}`
    const body = [
      `Name: ${name}`,
      `Email: ${email}`,
      `Company: ${company || '—'}`,
      `Team size: ${teamSize}`,
      '',
      'Message:',
      message,
    ].join('\n');
    const mailto = `mailto:contact@named.network?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
    setDraftReady(true);
  }

  return (
    <section className="form-section">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">Or send us a note</span>
          <h2>One form. One contact address.</h2>
          <p>Fill this in and we&rsquo;ll open an email to <code>contact@named.network</code> with the details already filled out.</p>
        </div>
        <div className="form-grid">
          <div className="form-meta">
            <h3>What to expect</h3>
            <p>This goes to <code>contact@named.network</code>. A real person reads it and replies in plain language.</p>
            <ul className="form-meta-list">
              <li><strong>Product question</strong> &mdash; best for “can named.network do this?”</li>
              <li><strong>Support</strong> &mdash; best for something broken or unclear</li>
              <li><strong>Pricing</strong> &mdash; best for plan or upgrade questions</li>
              <li><strong>Bug report</strong> &mdash; best when you can describe what happened clearly</li>
            </ul>
          </div>
          <form className="form-card" onSubmit={handleSubmit}>
            {!draftReady ? (
              <>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-light-muted)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 12 }}>What&rsquo;s this about?</div>
                <div className="topic-row">
                  {topics.map(t => (
                    <button type="button" key={t} className={`topic ${topic === t ? 'active' : ''}`} onClick={() => setTopic(t)}>{t}</button>
                  ))}
                </div>
                <div className="field-row">
                  <div className="field">
                    <label>Your name</label>
                    <input type="text" placeholder="Jane Cooper" value={name} onChange={(e) => setName(e.target.value)} required />
                  </div>
                  <div className="field">
                    <label>Work email</label>
                    <input type="email" placeholder="jane@acme.co" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                </div>
                <div className="field-row">
                  <div className="field">
                    <label>Company (optional)</label>
                    <input type="text" placeholder="Acme Corp" value={company} onChange={(e) => setCompany(e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Team size</label>
                    <select value={teamSize} onChange={(e) => setTeamSize(e.target.value)}>
                      <option>Just me</option>
                      <option>2 — 10</option>
                      <option>11 — 50</option>
                      <option>51 — 200</option>
                      <option>200+</option>
                    </select>
                  </div>
                </div>
                <div className="field">
                  <label>What&rsquo;s on your mind?</label>
                  <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell us what you&apos;re trying to do, what you&apos;ve tried, and where things go wrong. Concrete is better than polite." required></textarea>
                </div>
                <div className="form-submit">
                  <div className="legal">By submitting, you agree we can email you back. We won&rsquo;t add you to a marketing list. We don&rsquo;t even have one.</div>
                  <button type="submit" className="btn btn-accent btn-lg" style={{ minWidth: 200 }}>
                    <span>Email contact@named.network <Icon.arrow /></span>
                  </button>
                </div>
              </>
            ) : (
              <div style={{ padding: '24px 0', textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 18 }}>EMAIL DRAFT OPENED</div>
                <h3 style={{ fontFamily: 'var(--font-sans)', fontWeight: 400, fontSize: 32, letterSpacing: '-0.02em', margin: '0 0 14px' }}>Almost there.</h3>
                <p style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text-light-secondary)', margin: 0, lineHeight: 1.6 }}>Your mail app should now have a draft addressed to <code>contact@named.network</code> with these details filled in.</p>
                <button type="button" className="btn btn-accent btn-lg" style={{ marginTop: 28 }} onClick={() => setDraftReady(false)}>
                  <span>Send another <Icon.arrow /></span>
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const faqs = [
    { q: 'How fast do you actually reply?', a: 'Usually the same day on business days. If you include concrete details, we can help faster.' },
    { q: 'Do I need to file a ticket from the dashboard, or can I email?', a: 'Either. The dashboard ticket attaches workspace context automatically, so it\u2019s slightly faster on our end. Email works fine for everything else.' },
    { q: 'Is there a phone number?', a: 'For Business and Enterprise customers with active support contracts, yes — it\u2019s in your dashboard. For everyone else, async channels are how we keep response times this fast.' },
    { q: 'What should I include in the message?', a: 'Tell us what you are trying to do, what you expected, what happened instead, and anything that helps us reproduce or understand the problem.' },
    { q: 'Can I contact you about pricing or upgrades?', a: 'Yes. Use the same contact address. If it is a pricing question, say how many people or devices you are planning for.' },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section className="section section-light">
      <div className="container-narrow">
        <div className="section-h">
          <span className="section-label on-light">FAQ</span>
          <h2>Common contact questions.</h2>
        </div>
        <div className="faq-list">
          {faqs.map((f, i) => (
            <div key={i} className={`faq-item ${open === i ? 'open' : ''}`}>
              <button className="faq-q" onClick={() => setOpen(open === i ? -1 : i)}>
                <span>{f.q}</span><span className="plus">+</span>
              </button>
              <div className="faq-a">{f.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function App() {
  return (
    <>
      <SiteNav />
      <Hero />
      <Channels />
      <ContactForm />
      <FAQ />
      <SiteFooter />
    </>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);

